export interface StableStoryMetadata {
  storyId: string | null;
  description: string;
  topics: string[];
}

export interface StoryMetadataSnapshot {
  storyId: string;
  description: string;
  topics: string[];
  preserveSparseSnapshots: boolean;
}

type StoryMetadataFields = {
  aiResponse?: {
    responses: Array<{ title: string; response?: unknown }>;
    [key: string]: unknown;
  };
  clipThemesJson?: unknown;
};

const descriptionResponse = (fields: StoryMetadataFields) =>
  fields.aiResponse?.responses?.find(
    item => item.title?.toLowerCase() === 'description',
  );

const hasDescription = (fields: StoryMetadataFields) => {
  const response = descriptionResponse(fields)?.response;
  return typeof response === 'string' && response.trim().length > 0;
};

/**
 * A route transition can retain a partial processing snapshot even though a
 * full story fetch already contains generated metadata. Recover only fields
 * that are missing locally, so the poll cannot overwrite a user's newer edit.
 */
export function recoverMissingStoryMetadata<T extends StoryMetadataFields>(
  current: T,
  fresh: T,
  parseTopics: (raw: unknown) => string[],
): Pick<T, 'aiResponse' | 'clipThemesJson'> {
  let aiResponse = current.aiResponse;
  const incomingDescription = descriptionResponse(fresh);
  if (
    !hasDescription(current) &&
    hasDescription(fresh) &&
    incomingDescription
  ) {
    const currentResponses = current.aiResponse?.responses ?? [];
    const descriptionIndex = currentResponses.findIndex(
      item => item.title?.toLowerCase() === 'description',
    );
    const responses = [...currentResponses];
    if (descriptionIndex >= 0) {
      responses[descriptionIndex] = incomingDescription;
    } else {
      responses.push(incomingDescription);
    }
    aiResponse = {
      ...fresh.aiResponse,
      ...current.aiResponse,
      responses,
    };
  }

  const clipThemesJson =
    parseTopics(current.clipThemesJson).length === 0 &&
    parseTopics(fresh.clipThemesJson).length > 0
      ? fresh.clipThemesJson
      : current.clipThemesJson;

  return { aiResponse, clipThemesJson };
}

/**
 * AI metadata is written independently from the media pipeline. While a new
 * story is processing, a later Dato snapshot can therefore be structurally
 * valid but temporarily omit fields an earlier snapshot already contained.
 * Keep those fields monotonic for the active story during that window; once
 * processing is over, the canonical snapshot (including intentional clears)
 * wins again.
 */
export function stabilizeStoryMetadata(
  previous: StableStoryMetadata,
  incoming: StoryMetadataSnapshot,
): StableStoryMetadata {
  const sameStory = previous.storyId === incoming.storyId;
  if (!sameStory || !incoming.preserveSparseSnapshots) {
    return {
      storyId: incoming.storyId,
      description: incoming.description,
      topics: incoming.topics,
    };
  }

  return {
    storyId: incoming.storyId,
    description: incoming.description || previous.description,
    topics: incoming.topics.length > 0 ? incoming.topics : previous.topics,
  };
}
